import { useEffect, useRef, useState } from 'react';
import useReducedMotion from './useReducedMotion';

interface UseScrollRevealOptions {
    /** Fraction of the element that must be visible to trigger the reveal (0–1). */
    threshold?: number;
    /** Margin around the root, forwarded to `IntersectionObserver`. */
    rootMargin?: string;
    /** Reveal only once, or toggle every time visibility changes. Defaults to `true`. */
    triggerOnce?: boolean;
}

/**
 * Observes an element and reports when it has scrolled into view, so callers
 * can apply a reveal animation. Honors `prefers-reduced-motion`: when the
 * user opts out of motion, the element is reported as visible immediately
 * and no observation/animation happens.
 */
export function useScrollReveal<T extends HTMLElement>({
    threshold = 0.15,
    rootMargin = '0px 0px -10% 0px',
    triggerOnce = true,
}: UseScrollRevealOptions = {}) {
    const ref = useRef<T | null>(null);
    const prefersReducedMotion = useReducedMotion();
    const [isVisible, setIsVisible] = useState(prefersReducedMotion);

    useEffect(() => {
        if (prefersReducedMotion) {
            setIsVisible(true);
            return;
        }

        const node = ref.current;
        if (!node || typeof IntersectionObserver === 'undefined') {
            setIsVisible(true);
            return;
        }

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                    if (triggerOnce) observer.disconnect();
                } else if (!triggerOnce) {
                    setIsVisible(false);
                }
            },
            { threshold, rootMargin }
        );

        observer.observe(node);
        return () => observer.disconnect();
    }, [prefersReducedMotion, threshold, rootMargin, triggerOnce]);

    return { ref, isVisible, prefersReducedMotion };
}

export default useScrollReveal;
